import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import {
  IResourceComponentsProps,
  useParsed,
  useOne,
  useDelete,
} from "@refinedev/core";
import { DateField, List, useDataGrid } from "@refinedev/mui";

import { Typography, Stack, CircularProgress, IconButton } from "@mui/material";
import {
  GridColumns,
  DataGrid,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { Delete, Edit, RemoveRedEye } from "@mui/icons-material";

import { Icon, Point } from "leaflet";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

import { useModal } from "components/Modal";

import { theme } from "constants/theme";

import { checkLocation } from "utils/location";
import { queryClient } from "App";
import { AttendanceEntity, FormEntity } from "generated";

export type RecordListType = {
  count: number;
  attendances: AttendanceEntity[];
};

const destinationIcon = new Icon({
  iconUrl: require("assets/images/destination-map-marker.png"),
  iconRetinaUrl: require("assets/images/destination-map-marker.png"),
  iconSize: new Point(25, 40),
});

export const ModalInfoContent: React.FC<{
  longitude: number;
  latitude: number;
}> = ({ longitude, latitude }) => {
  const [myCoords, setMyCoords] = useState<{
    longitude: number | undefined;
    latitude: number | undefined;
  }>({
    longitude: undefined,
    latitude: undefined,
  });

  useEffect(() => {
    checkLocation({
      onSuccess(data) {
        setMyCoords({
          longitude: data.coords.longitude,
          latitude: data.coords.latitude,
        });
      },
    });
  }, []);

  return (
    <MapContainer
      style={{
        height: "350px",
      }}
      center={[latitude, longitude]}
      zoom={100}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {typeof myCoords.longitude !== "undefined" &&
      typeof myCoords.latitude !== "undefined" ? (
        <Marker position={[myCoords.latitude, myCoords.longitude]}>
          <Popup>My location</Popup>
        </Marker>
      ) : null}

      <Marker icon={destinationIcon} position={[latitude, longitude]}>
        <Popup>Destination</Popup>
      </Marker>
    </MapContainer>
  );
};

export const RecordList: React.FC<IResourceComponentsProps> = () => {
  const parsedData = useParsed();
  const formId = parsedData?.params?.formId;

  const [modalType, setModalType] = useState<"view" | "delete">("view");
  const { Modal, setOpen } = useModal({ defaultOpen: false });

  const [record, setRecord] = useState<AttendanceEntity>();

  const fetchResourceString = `attendances?formId=${formId}`;
  const { dataGridProps } = useDataGrid({
    resource: fetchResourceString,
  });

  const count = (dataGridProps.rows as unknown as RecordListType)?.count || 0;

  dataGridProps.rows =
    (dataGridProps.rows as unknown as RecordListType)?.attendances || [];

  const { data: formData, isLoading: formIsLoading } = useOne<FormEntity>({
    resource: `forms`,
    id: formId ?? "",
    queryOptions: {
      enabled: !!dataGridProps?.rows?.length,
    },
  });

  const { mutate: deleteRecordMutate, isLoading: isDeletingRecord } =
    useDelete();

  const {
    filterMode,
    filterModel,
    onFilterModelChange,
    sortingMode,
    sortModel,
    onSortModelChange,
    paginationMode,
    page,
    onPageChange,
    pageSize,
    onPageSizeChange,
    ...restDataGridProps
  } = dataGridProps;

  const columns = React.useMemo<GridColumns<AttendanceEntity>>(
    () => [
      {
        field: "createdAt",
        flex: 1,
        headerName: "Date",
        minWidth: 150,
        filterable: false,
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "formId",
        flex: 1,
        headerName: "Form",
        minWidth: 250,
        filterable: false,
        sortable: false,
        renderCell() {
          return formIsLoading ? (
            <CircularProgress />
          ) : (
            formData?.data?.label || "-"
          );
        },
      },

      {
        field: "userName",
        flex: 1,
        headerName: "User Name",
        minWidth: 250,
        filterable: false,
      },

      {
        field: "userId",
        flex: 1,
        headerName: "User Id",
        minWidth: 250,
        filterOperators: getGridStringOperators().filter((op) => {
          return op.value === "equals";
        }),
        renderCell({ value }) {
          return <Typography>{value}</Typography>;
        },
      },

      {
        field: "location",
        flex: 1,
        headerName: "Coords",
        filterable: false,
        sortable: false,
        minWidth: 200,

        renderCell(params) {
          return (
            <Stack>
              <Typography>Longitude: {params.value[0]}</Typography>
              <Typography>Latitude: {params.value[1]}</Typography>
            </Stack>
          );
        },
      },

      {
        field: "identifier",
        flex: 1,
        headerName: "Identifier",
        filterable: false,
        sortable: false,
        minWidth: 200,
      },
      {
        field: "description",
        flex: 1,
        headerName: "Description",
        filterable: false,
        sortable: false,
        minWidth: 200,
      },
      {
        field: "actions",
        headerName: "Actions",
        filterable: false,
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <IconButton
                onClick={() => {
                  setRecord(row);
                  setModalType("view");
                  setOpen(true);
                }}
              >
                <RemoveRedEye color="primary" />
              </IconButton>
              <NavLink to={`/forms/show/${row.formId}?recordId=${row.id}`}>
                <Edit color="primary" />
              </NavLink>

              <IconButton
                disabled={isDeletingRecord}
                onClick={() => {
                  setRecord(row);
                  setModalType("delete");
                  setOpen(true);
                }}
              >
                <Delete color="primary" />
              </IconButton>
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 120,
      },
    ],
    [formData?.data, formIsLoading, setOpen, isDeletingRecord]
  );

  const deleteRecordHandler = () => {
    deleteRecordMutate(
      {
        resource: "attendances",
        id: record?.id || "",
        successNotification: () => {
          return {
            description: "Record delete",
            message: `Record successfully deleted.`,
            type: "success",
          };
        },
        errorNotification: (errors) => {
          return {
            description: errors?.message,
            message: errors?.errors?.length
              ? errors?.errors?.[0]?.messages?.[0]?.toUpperCase?.()
              : "",
            type: "error",
          };
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            "default",
            fetchResourceString,
            "list",
          ]);
        },
      }
    );
  };

  return (
    <List breadcrumb={""} title={count ? `Records (${count})` : undefined}>
      <DataGrid
        columns={columns}
        {...restDataGridProps}
        filterMode={filterMode}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortingMode={"client"}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        paginationMode={paginationMode}
        page={page}
        onPageChange={onPageChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        autoHeight
      />

      {modalType === "view" ? (
        <Modal
          title="रेकर्ड"
          cancelTitle="रद्द गर्नुहोस्"
          showCancel={false}
          okTitle="बन्द गर्नुहोस्"
          id="location"
        >
          {record ? (
            <Stack gap={theme.gap}>
              {record.identifier ? (
                <>
                  <Typography>पहिचानकर्ता</Typography>
                  <Typography>{record.identifier}</Typography>
                </>
              ) : null}

              {record.description ? (
                <>
                  <Typography>विवरण</Typography>
                  <Typography>{record.description}</Typography>
                </>
              ) : null}

              {record.image ? (
                <>
                  <Typography>फोटो</Typography>
                  <img src={record.image} alt="identifier" />
                </>
              ) : null}

              {record.location ? (
                <>
                  <Typography>स्थान</Typography>

                  <ModalInfoContent
                    longitude={record.location[0]}
                    latitude={record.location[1]}
                  />
                </>
              ) : null}
            </Stack>
          ) : null}
        </Modal>
      ) : modalType === "delete" ? (
        <Modal
          title="के तपाइँ यो रेकर्ड मेटाउन निश्चित हुनुहुन्छ ?"
          cancelTitle="रद्द गर्नुहोस्"
          okTitle="मेटाउनुहोस्"
          onOk={deleteRecordHandler}
          id="delete"
        />
      ) : null}
    </List>
  );
};
